import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/register">
            <Button variant="ghost" size="icon" data-testid="back-to-register-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#547d91]" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Terms of Service</h1>
          </div>
        </div>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6 md:p-8 max-w-none text-slate-700 leading-relaxed space-y-4 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:mb-2 [&_li]:text-slate-700" data-testid="terms-content">
            <h2 className="text-xl font-semibold text-[#547d91] mt-0">Pocket BA — Powered by Investeps Property</h2>
            <p className="text-sm text-slate-500">Effective Date: 1 June 2026</p>

            <h3>1. Acceptance of Terms</h3>
            <p>
              These Terms of Service ("Terms") govern your access to and use of the Pocket BA application and
              related services ("App"), operated by Investeps Property ("we", "us", or "our"). By creating an
              account or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.
            </p>

            <h3>2. AI-Generated Content Disclaimer</h3>
            <p>
              The App uses artificial intelligence and third-party data sources to generate property due-diligence
              reports, price and rental estimates, and conversational insights ("AI Content"). AI Content is provided
              for <strong>general information purposes only</strong> and may be incomplete, inaccurate, or out of date.
            </p>
            <ul>
              <li>AI Content is <strong>not</strong> financial, legal, tax, valuation, or investment advice.</li>
              <li>Estimates are indicative only and are not formal valuations or guarantees of value, rent, or return.</li>
              <li>You must independently verify all information and seek qualified professional advice before making any decision.</li>
              <li>We do not warrant the accuracy, completeness, or reliability of any AI Content or third-party data.</li>
            </ul>

            <h3>3. Third-Party Data</h3>
            <p>
              Property data may be sourced from third-party providers and public or government records. We are not
              responsible for the accuracy, availability, or currency of third-party data, and access to certain
              features may depend on the continued availability of those providers.
            </p>

            <h3>4. Subscriptions, Plans &amp; Billing</h3>
            <ul>
              <li>
                <strong>Pocket BA Lite (Free):</strong> A free tier that includes access to the App except the AI
                Property Researcher tools. No payment is required.
              </li>
              <li>
                <strong>AI Buyers Agent (Paid):</strong> Available on a <strong>Monthly plan at $99.00 AUD/month</strong>{" "}
                or an <strong>Annual plan at $594.00 AUD/year</strong>, both inclusive of 10% GST.
              </li>
              <li>
                <strong>Free trial:</strong> New subscribers may receive a 7-day free trial (or an extended trial via a
                referral partner). Your payment method is collected at sign-up but not charged until the trial ends.
                You may cancel before the trial ends to avoid being charged.
              </li>
              <li>
                <strong>Auto-renewal:</strong> Paid subscriptions renew automatically each billing period (monthly or
                annually) at the then-current price until cancelled.
              </li>
              <li>
                <strong>Referral discounts:</strong> Where a valid referral partner code applies, the associated discount
                is reflected at checkout and on renewals for as long as it remains valid.
              </li>
              <li>
                <strong>Taxes:</strong> Prices are shown in Australian Dollars (AUD) and are inclusive of GST where applicable.
              </li>
            </ul>

            <h3>5. Cancellation &amp; Downgrade</h3>
            <p>
              You may cancel your paid subscription at any time from within the App or via the billing portal. When you
              cancel, you retain access to the AI Buyers Agent until the end of your current paid period, after
              which your account reverts to <strong>Pocket BA Lite</strong> and you keep free access to the rest of the App.
              Except where required by law, payments already made are non-refundable, and we do not provide pro-rated
              refunds for partial periods.
            </p>

            <h3>6. Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, Investeps Property, its officers, employees, and suppliers will not
              be liable for any indirect, incidental, special, consequential, or punitive loss or damage, or any loss of
              profits, revenue, data, or opportunity, arising out of or in connection with your use of (or inability to
              use) the App or any reliance on AI Content or third-party data.
            </p>
            <p>
              Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy that
              cannot lawfully be excluded under the Australian Consumer Law. Where our liability cannot be excluded but
              can be limited, our liability is limited, at our option, to re-supplying the relevant service or paying the
              cost of having it re-supplied.
            </p>

            <h3>7. Acceptable Use</h3>
            <p>You agree not to misuse the App, including by attempting to reverse engineer, scrape, resell, or
              overload the service, or by using it for any unlawful purpose.</p>

            <h3>8. Changes to These Terms</h3>
            <p>
              We may update these Terms from time to time. The latest version will always be available within the App.
              Continued use of the App after changes take effect constitutes acceptance of the updated Terms.
            </p>

            <h3>9. Contact Us</h3>
            <p>If you have any questions about these Terms, please contact:</p>
            <address className="not-italic bg-slate-50 p-4 rounded-lg">
              <strong>Investeps Property</strong><br />
              844 Old Cleveland Rd<br />
              Carina QLD 4152<br />
              Email: <a href="mailto:info@investepsproperty.com" className="text-[#547d91]">info@investepsproperty.com</a>
            </address>
          </CardContent>
        </Card>

        {/* Back to Register Button */}
        <div className="mt-6 text-center">
          <Link to="/register">
            <Button className="bg-[#547d91] hover:bg-[#456a7a]" data-testid="return-to-register-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Registration
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
